/**
 * Default Card Component
 *
 * Generic fallback card rendered for resource types that do not have
 * a dedicated registered card component. Displays the core fields
 * (image, name, address, description, links) without type-specific logic.
 * Supports both feed card and map popup rendering via the `variant` prop.
 */

import { h } from 'preact';
import htm from 'htm';
import { IconMapPin } from '../../../components/Icons.js';
import { CardMedia } from './shared/CardMedia.js';
import { CardDescription } from './shared/CardDescription.js';
import { CardLinks } from './shared/CardLinks.js';
import { pickText, label, buildFullAddress } from './shared/fields.js';

const html = htm.bind(h);

/**
 * Renders a generic fallback result card or map popup.
 *
 * @param {Object} props
 * @param {Object} props.item - The resource item data.
 * @param {Object} [props.block] - Parent block data.
 * @param {Object} props.ctx - View context with `t`, `config`, and `onShowPinOnMap`.
 * @param {string} [props.variant='card'] - 'card' for blocks feed or 'popup' for map infowindow.
 * @param {Function} [props.onClose] - Callback when the popup close button is clicked.
 */
export function DefaultCard({ item, block, ctx, variant = 'card', onClose }) {
  const prof = item?.profile || {};
  const isPopup = variant === 'popup';

  const tag = pickText(item, 'categories', 'category', 'tag', 'type');
  const name = pickText(item, 'name', 'label', 'title');
  const address = buildFullAddress(item);
  const phone = pickText(item, 'phone');
  const description = pickText(item, 'description', 'content');
  const website = pickText(item, 'website');
  const bookingUrl = pickText(item, 'booking_url');
  const sourceUrl = pickText(item, 'source_url', 'url');
  const mapUrl = pickText(item, 'map_url');

  const latitude = prof.latitude ?? item?.latitude;
  const longitude = prof.longitude ?? item?.longitude;
  const isClickableAddress = !isPopup && latitude != null && longitude != null && typeof ctx?.onShowPinOnMap === 'function';

  const handleAddressClick = (e) => {
    if (isClickableAddress) {
      e.preventDefault();
      ctx.onShowPinOnMap({
        points: [
          {
            ...item,
            category: tag,
            latitude: latitude,
            longitude: longitude,
            image_url: item?.image_url || pickText(item, 'photos') || block?.image_url
          }
        ],
        center_latitude: latitude,
        center_longitude: longitude,
        zoom: 15
      });
    }
  };

  const containerClass = isPopup
    ? 'mta-result-card mta-card--popup mta-map-popup-card mta-map-popup-theme'
    : 'mta-result-card';

  return html`
    <div class="${containerClass}">
      <${CardMedia} item=${item} block=${block} ctx=${ctx} variant=${variant} onClose=${onClose} />

      ${tag ? html`<div class="mta-card-tag">${tag}</div>` : ''}
      <h3 class="mta-card-title">${name}</h3>

      <div class="mta-card-meta">
        ${address
          ? html`
              <div
                class=${`mta-card-meta-item ${isClickableAddress ? 'mta-clickable-address' : ''}`}
                onClick=${handleAddressClick}
              >
                <span class="mta-item-pin-icon"><${IconMapPin} /></span>
                <span>${address}</span>
              </div>
            `
          : ''}
        ${phone
          ? html`
              <div class="mta-card-meta-item" title="Phone">
                <span>\uD83D\uDCDE ${phone}</span>
              </div>
            `
          : ''}
      </div>

      <${CardDescription} description=${description} ctx=${ctx} variant=${variant} />

      <${CardLinks} links=${[
        { href: mapUrl, text: label(ctx, 'show_on_map', 'Show on map') },
        { href: bookingUrl, text: label(ctx, 'book_now', 'Book now') },
        { href: sourceUrl, text: label(ctx, 'show_on_site', 'Show on site') },
        { href: website, text: label(ctx, 'website', 'Website') }
      ]} />
    </div>
  `;
}
